import { type AdminProductReview, ProductReviewReasonBody } from "@cleanbudget/contract/product-review-admin";
import { Alert, AlertDescription } from "@cleanbudget/ui/components/alert";
import { Button } from "@cleanbudget/ui/components/button";
import { IconAlertCircle, IconCheck, IconCircleCheck, IconEyeOff, IconX } from "@tabler/icons-react";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { DetailFacts, DetailLayout, DetailList, DetailSection } from "@/components/detail-layout";
import { Form, FormError, SubmitButton, TextareaField, useZodForm } from "@/components/form/form";
import { PageHeader } from "@/components/page-header";
import { apiErrorTag } from "@/lib/api-error";
import { formatDateTime } from "@/lib/format";
import { orpc } from "@/lib/orpc";
import { ReviewStatusBadge, Stars } from "./-components/review-bits";

const reviewQuery = (reviewId: string) => orpc.productReviewAdmin.get.queryOptions({ input: { reviewId } });

/**
 * One review (#260, stories 119 to 121): the buyer's words as written (read only: there is no way
 * to change them), the order, customer and product it belongs to, and the decision: approve or
 * reject a pending review, take a published one offline. Rejecting and taking offline ask for an
 * internal reason.
 */
export const Route = createFileRoute("/_admin/product-review/$reviewId")({
	loader: ({ context, params }) => context.queryClient.ensureQueryData(reviewQuery(params.reviewId)),
	component: ReviewPage,
});

function ReviewPage() {
	const { t } = useTranslation("productReview");
	const { reviewId } = Route.useParams();
	const { data: review } = useSuspenseQuery(reviewQuery(reviewId));

	return (
		<DetailLayout
			header={
				<PageHeader
					back={<BackLink to="/product-review">{t("title")}</BackLink>}
					title={t("detail.title", { name: review.firstName ?? t("anonymousBuyer") })}
					description={
						<span className="flex flex-wrap items-center gap-2">
							<ReviewStatusBadge status={review.status} />
							<span>{review.product.name}</span>
						</span>
					}
				/>
			}
			aside={<DecisionSection review={review} />}
		>
			<WordsSection review={review} />
			<PurchaseSection review={review} />
		</DetailLayout>
	);
}

function WordsSection({ review }: { review: AdminProductReview }) {
	const { t } = useTranslation("productReview");
	const none = <span className="text-muted-foreground">{t("detail.none")}</span>;
	return (
		<DetailSection title={t("detail.review")} description={t("detail.reviewDescription")}>
			<DetailList
				items={[
					{ label: t("detail.stars"), value: <Stars stars={review.stars} /> },
					{ label: t("detail.firstName"), value: review.firstName ?? none },
					{ label: t("detail.heading"), value: review.title ?? none },
					{ label: t("detail.written"), value: formatDateTime(review.writtenAt) },
				]}
			/>
			<div className="mt-4 flex flex-col gap-1">
				<span className="text-muted-foreground">{t("detail.body")}</span>
				<p className="break-words whitespace-pre-wrap">{review.body ?? none}</p>
			</div>
		</DetailSection>
	);
}

function PurchaseSection({ review }: { review: AdminProductReview }) {
	const { t } = useTranslation("productReview");
	const linkClass = "font-medium underline-offset-4 hover:underline";
	return (
		<DetailSection title={t("detail.purchase")} description={t("detail.verified")}>
			<DetailList
				items={[
					{
						label: t("detail.product"),
						value: (
							<Link to="/product/$productId" params={{ productId: review.product.id }} className={linkClass}>
								{review.product.name}
							</Link>
						),
					},
					{
						label: t("detail.order"),
						value: (
							<Link to="/order/$orderId" params={{ orderId: review.order.id }} className={linkClass}>
								{review.order.orderNumber}
							</Link>
						),
					},
					{ label: t("detail.placedAt"), value: formatDateTime(review.order.placedAt) },
					{ label: t("detail.orderedBy"), value: review.order.name },
					{
						label: t("detail.customer"),
						value: (
							<span className="flex flex-col">
								<Link
									to="/customer/$customerId"
									params={{ customerId: review.customer.id }}
									className={linkClass}
								>
									{review.customer.anonymised
										? t("detail.anonymised")
										: (review.customer.name ?? review.customer.email)}
								</Link>
								{review.customer.anonymised || review.customer.name === null ? null : (
									<span className="text-xs text-muted-foreground">{review.customer.email}</span>
								)}
							</span>
						),
					},
				]}
			/>
		</DetailSection>
	);
}

/** Writes go to this review, the list and the start page's work queue. */
function useDecisionWritten() {
	const queryClient = useQueryClient();
	return (updated: AdminProductReview) => {
		queryClient.setQueryData(
			orpc.productReviewAdmin.get.queryKey({ input: { reviewId: updated.id } }),
			updated,
		);
		return Promise.all([
			queryClient.invalidateQueries({ queryKey: orpc.productReviewAdmin.list.key() }),
			queryClient.invalidateQueries({ queryKey: orpc.workQueue.get.key() }),
		]);
	};
}

function DecisionSection({ review }: { review: AdminProductReview }) {
	const { t } = useTranslation("productReview");
	const [open, setOpen] = useState<"reject" | "takeOffline" | null>(null);
	const [approvedNow, setApprovedNow] = useState(false);
	const written = useDecisionWritten();
	const approve = useMutation({ ...orpc.productReviewAdmin.approve.mutationOptions(), onSuccess: written });

	const decided =
		review.reviewedAt === null
			? []
			: [
					{
						label: t("decision.decided"),
						value: review.reviewedBy
							? t("decision.decidedBy", {
									name: review.reviewedBy.name,
									date: formatDateTime(review.reviewedAt),
								})
							: formatDateTime(review.reviewedAt),
					},
				];
	const reason =
		review.rejectionReason === null
			? []
			: [
					{
						label: t("decision.reason"),
						value: <span className="break-words whitespace-pre-wrap">{review.rejectionReason}</span>,
					},
				];

	return (
		<DetailSection title={t("decision.title")}>
			<div className="flex flex-col gap-4">
				<p className="text-sm text-muted-foreground">
					{match(review.status)
						.with("pending", () => t("decision.pendingHelp"))
						.with("approved", () => t("decision.approvedHelp"))
						.with("rejected", () => t("decision.rejectedHelp"))
						.exhaustive()}
				</p>
				{decided.length + reason.length > 0 ? <DetailFacts facts={[...reason, ...decided]} /> : null}
				{approvedNow && review.status === "approved" ? (
					<Alert>
						<IconCircleCheck aria-hidden />
						<AlertDescription>{t("decision.approved")}</AlertDescription>
					</Alert>
				) : null}
				{approve.isError ? (
					<Alert variant="destructive">
						<IconAlertCircle aria-hidden />
						<AlertDescription>
							{apiErrorTag(approve.error) === "ReviewDecisionNotAllowed"
								? t("decision.notAllowed")
								: t("decision.failed")}
						</AlertDescription>
					</Alert>
				) : null}
				{open === null ? (
					match(review.status)
						.with("pending", () => (
							<div className="flex flex-wrap gap-2">
								<Button
									disabled={approve.isPending}
									onClick={() =>
										approve.mutate({ reviewId: review.id }, { onSuccess: () => setApprovedNow(true) })
									}
								>
									<IconCheck aria-hidden />
									{t("decision.approve")}
								</Button>
								<Button variant="outline" onClick={() => setOpen("reject")}>
									<IconX aria-hidden />
									{t("decision.reject")}
								</Button>
							</div>
						))
						.with("approved", () => (
							<div>
								<Button variant="outline" onClick={() => setOpen("takeOffline")}>
									<IconEyeOff aria-hidden />
									{t("decision.takeOffline")}
								</Button>
							</div>
						))
						.with("rejected", () => null)
						.exhaustive()
				) : (
					<ReasonForm review={review} decision={open} onDone={() => setOpen(null)} />
				)}
			</div>
		</DetailSection>
	);
}

/** Rejecting a pending review or taking a published one offline: the internal reason, then the decision. */
function ReasonForm({
	review,
	decision,
	onDone,
}: {
	review: AdminProductReview;
	decision: "reject" | "takeOffline";
	onDone: () => void;
}) {
	const { t } = useTranslation("productReview");
	const written = useDecisionWritten();
	const reject = useMutation({ ...orpc.productReviewAdmin.reject.mutationOptions(), onSuccess: written });
	const takeOffline = useMutation({
		...orpc.productReviewAdmin.takeOffline.mutationOptions(),
		onSuccess: written,
	});
	const form = useZodForm(ProductReviewReasonBody, { defaultValues: { reason: "" } });

	const onSubmit = async (values: ProductReviewReasonBody) => {
		try {
			const input = { reviewId: review.id, ...values };
			await (decision === "reject" ? reject.mutateAsync(input) : takeOffline.mutateAsync(input));
			onDone();
		} catch (error) {
			form.setError("root", {
				type: "server",
				message:
					apiErrorTag(error) === "ReviewDecisionNotAllowed" ? t("decision.notAllowed") : t("decision.failed"),
			});
		}
	};

	const titleId = `${decision}-title`;
	return (
		<section aria-labelledby={titleId} className="flex flex-col gap-4 rounded-xl border border-border p-4">
			<h3 id={titleId} className="font-medium">
				{decision === "reject" ? t("decision.rejectTitle") : t("decision.takeOfflineTitle")}
			</h3>
			<Form form={form} onSubmit={onSubmit} className="gap-4">
				<TextareaField
					control={form.control}
					name="reason"
					label={t("decision.reasonField")}
					description={t("decision.reasonHint")}
					rows={3}
				/>
				<FormError control={form.control} />
				<div className="flex flex-wrap gap-2">
					<SubmitButton control={form.control} variant="destructive">
						{decision === "reject" ? t("decision.reject") : t("decision.takeOffline")}
					</SubmitButton>
					<Button type="button" variant="outline" onClick={onDone}>
						{t("decision.cancel")}
					</Button>
				</div>
			</Form>
		</section>
	);
}
